import { COMPANY_PDF_LOGO } from "@/lib/brand";
import { formatDate, formatNumber } from "@/lib/format";

export interface ProductionOrderPrintItem {
  profile?: string;
  gauge?: string;
  color?: string;
  description?: string;
  length: number;
  quantity: number;
  unit: string;
  totalQuantity: number;
  remarks?: string;
}

export interface ProductionOrderPrintData {
  number: string;
  productionDate?: string;
  status?: string;
  customerName: string;
  customerPhone?: string;
  quotationRef?: string;
  gauge?: string;
  profile?: string;
  color?: string;
  items: ProductionOrderPrintItem[];
  totalSheets: number;
  totalLength: number;
  estimatedMaterialUsage: number;
  remarks?: string;
}

export interface ProductionOrderPrintCompany {
  name: string;
  address: string;
  phone: string;
  email: string;
}

function effectiveLength(length: number) {
  return length > 0 ? length : 1;
}

function statusLabel(status?: string) {
  if (status === "in_production") return "In production";
  if (status === "completed") return "Completed";
  if (status === "pending") return "Pending";
  return status || "—";
}

// Compose a single human-readable description per line from the data the line
// already carries. The production_order_items table has no dedicated
// description column, so we concatenate the meaningful attributes in the order
// gauge → color → profile → remarks (e.g. "0.35MM RAINBOW DSR MSL RAL 8019 R/S").
function composeDescription(it: ProductionOrderPrintItem) {
  if (it.description && it.description.trim()) return it.description.trim();
  const parts = [it.gauge, it.color, it.profile, it.remarks]
    .map((p) => (p ? String(p).trim() : ""))
    .filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
}

function lineLength(it: ProductionOrderPrintItem) {
  return effectiveLength(Number(it.length || 0));
}

function lineTotalLength(it: ProductionOrderPrintItem) {
  const len = lineLength(it);
  const qty = Number(it.quantity || 0);
  const total = Number(it.totalQuantity || 0);
  return total > 0 ? total : len * qty;
}

interface PrintGroup {
  key: string;
  items: ProductionOrderPrintItem[];
}

// Group lines by profile (the only sensible per-line grouping key available on
// production_order_items). Falls back to a single group when no profile is set,
// so a flat list renders one numbered sequence + one Total + Grand Total.
function buildGroups(items: ProductionOrderPrintItem[]): PrintGroup[] {
  const groups: PrintGroup[] = [];
  const byKey = new Map<string, PrintGroup>();
  for (const it of items) {
    const key = (it.profile || "").trim() || "__ungrouped__";
    let group = byKey.get(key);
    if (!group) {
      group = { key, items: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.items.push(it);
  }
  return groups;
}

export default function ProductionOrderPrint({
  order,
  company,
  preparedBy = "System",
}: {
  order: ProductionOrderPrintData;
  company: ProductionOrderPrintCompany;
  preparedBy?: string;
}) {
  const metaLeft = [
    { label: "Customer", value: order.customerName || "—" },
    { label: "Phone", value: order.customerPhone || "—" },
    order.quotationRef
      ? { label: "Quotation Ref", value: order.quotationRef }
      : null,
    order.gauge ? { label: "Gauge", value: order.gauge } : null,
    order.profile ? { label: "Profile", value: order.profile } : null,
    order.color ? { label: "Color", value: order.color } : null,
  ].filter(Boolean) as Array<{ label: string; value: string }>;

  const metaRight = [
    { label: "Production Order No.", value: order.number },
    {
      label: "Production Date",
      value: order.productionDate
        ? formatDate(new Date(order.productionDate).getTime())
        : "—",
    },
    { label: "Status", value: statusLabel(order.status) },
  ].filter(Boolean) as Array<{ label: string; value: string }>;

  const groups = buildGroups(order.items);
  let grandQty = 0;
  let grandTotalLength = 0;

  return (
    <div
      className="print-area relative bg-white text-slate-900 p-5 font-sans overflow-hidden"
      style={{ width: "800px" }}
    >
      <div className="flex justify-between items-start border-b-2 border-slate-300 pb-2">
        <div className="flex items-center gap-4">
          <img
            src={COMPANY_PDF_LOGO}
            alt="Company logo"
            className="h-20 w-auto object-contain"
          />
          <div>
            <div className="text-lg font-bold uppercase leading-tight">
              {company.name}
            </div>
            <div className="text-[11px] mt-0.5">{company.address}</div>
            <div className="text-[11px]">Tel: {company.phone}</div>
            <div className="text-[11px]">Email: {company.email}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xl font-bold tracking-wide leading-tight">
            PRODUCTION ORDER
          </div>
          <div className="text-sm font-mono mt-0.5">{order.number}</div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-0.5 text-[13px] leading-tight">
        <div className="space-y-0.5">
          {metaLeft.map((row) => (
            <div key={row.label}>
              <span className="text-[10px] uppercase text-slate-500">
                {row.label}:{" "}
              </span>
              <span className="font-semibold">{row.value}</span>
            </div>
          ))}
        </div>
        <div className="space-y-0.5">
          {metaRight.map((row) => (
            <div key={row.label}>
              <span className="text-[10px] uppercase text-slate-500">
                {row.label}:{" "}
              </span>
              <span className="font-semibold">{row.value}</span>
            </div>
          ))}
        </div>
      </div>

      <table className="w-full mt-3 text-[11px] border-collapse">
        <thead>
          <tr className="bg-slate-100">
            <th className="border border-slate-300 px-2 py-1.5 text-center w-10">
              S.No
            </th>
            <th className="border border-slate-300 px-2 py-1.5 text-left">
              Product Description
            </th>
            <th className="border border-slate-300 px-2 py-1.5 text-right w-28">
              Length (mtr)
            </th>
            <th className="border border-slate-300 px-2 py-1.5 text-right w-24">
              Qty
            </th>
            <th className="border border-slate-300 px-2 py-1.5 text-right w-32">
              Total Length (mtr)
            </th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group, gi) => {
            let groupQty = 0;
            let groupTotalLength = 0;
            const rows = group.items.map((it, i) => {
              const len = lineLength(it);
              const qty = Number(it.quantity || 0);
              const total = lineTotalLength(it);
              groupQty += qty;
              groupTotalLength += total;
              return (
                <tr key={`${gi}-${i}`}>
                  <td className="border border-slate-300 px-2 py-1.5 text-center align-middle">
                    {i + 1}
                  </td>
                  <td className="border border-slate-300 px-2 py-1.5 align-middle">
                    {composeDescription(it)}
                  </td>
                  <td className="border border-slate-300 px-2 py-1.5 text-right font-mono align-middle">
                    {formatNumber(len)}
                  </td>
                  <td className="border border-slate-300 px-2 py-1.5 text-right font-mono align-middle">
                    {formatNumber(qty)}
                  </td>
                  <td className="border border-slate-300 px-2 py-1.5 text-right font-mono align-middle">
                    {formatNumber(total)}
                  </td>
                </tr>
              );
            });
            grandQty += groupQty;
            grandTotalLength += groupTotalLength;
            return [
              ...rows,
              <tr key={`${gi}-total`} className="bg-slate-50 font-semibold">
                <td
                  className="border border-slate-300 px-2 py-1.5 text-right uppercase text-[10px] tracking-wide align-middle"
                  colSpan={3}
                >
                  Total
                </td>
                <td className="border border-slate-300 px-2 py-1.5 text-right font-mono align-middle">
                  {formatNumber(groupQty)}
                </td>
                <td className="border border-slate-300 px-2 py-1.5 text-right font-mono align-middle">
                  {formatNumber(groupTotalLength)}
                </td>
              </tr>,
            ];
          })}
          <tr className="bg-slate-100 font-bold border-t-2 border-slate-400">
            <td
              className="border border-slate-300 px-2 py-2 text-right uppercase text-[10px] tracking-wide align-middle"
              colSpan={3}
            >
              Grand Total
            </td>
            <td className="border border-slate-300 px-2 py-2 text-right font-mono align-middle">
              {formatNumber(grandQty)}
            </td>
            <td className="border border-slate-300 px-2 py-2 text-right font-mono align-middle">
              {formatNumber(grandTotalLength)}
            </td>
          </tr>
        </tbody>
      </table>

      <div className="mt-3 grid grid-cols-2 gap-4 text-[13px] border-t border-slate-300 pt-3">
        <div>
          <div className="text-[10px] uppercase text-slate-500">Total Sheets</div>
          <div className="font-semibold font-mono mt-0.5">
            {formatNumber(order.totalSheets)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-slate-500">
            Estimated Material Usage
          </div>
          <div className="font-semibold font-mono mt-0.5">
            {formatNumber(order.estimatedMaterialUsage)}
          </div>
        </div>
      </div>

      {order.remarks ? (
        <p className="mt-3 text-[10px] text-slate-600">
          <strong>Remarks:</strong> {order.remarks}
        </p>
      ) : null}

      <div className="mt-3 pt-2 border-t border-slate-200 grid grid-cols-2 gap-8 text-[11px]">
        <div>
          <div className="text-slate-500 uppercase tracking-wide">
            Prepared By
          </div>
          <div className="font-semibold mt-0.5">{preparedBy}</div>
          <div className="mt-6 border-b border-slate-400" />
        </div>
        <div>
          <div className="text-slate-500 uppercase tracking-wide">
            Approved By (Signature)
          </div>
          <div className="mt-6 border-b border-slate-400" />
        </div>
      </div>
    </div>
  );
}
